"use client";

import React, {
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import { Box, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import { CustomTextField } from "../formsComponents";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import { api } from "../../api/api";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useBillingCompanyContext } from "../../providers";
import { ILeadSource, initialLeadSource } from "../../redux/leads/leads.types";
import { CreateLeadSourceDialog } from "../../views/leads/components/LeadSource/CreateLeadSourceDialog";

export interface ILeadSourceAutoSearch {
  label?: string;
  value: any;
  disabled?: boolean;
  source_name?: string;
  error?: string | boolean;
  helperText?: string;
  hideAddButton?: boolean;
  onSelect: (value: ILeadSource) => void;
  /** When true, applies a yellow highlight border to indicate filter is active */
  isHighlighted?: boolean;
}

export const LeadSourceAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  ILeadSourceAutoSearch
>((props, ref) => {
  const {
    value,
    disabled,
    label,
    source_name,
    hideAddButton,
    onSelect,
    error,
    helperText,
    isHighlighted = false,
  } = props;

  const [options, setOptions] = useState<readonly ILeadSource[]>([]);
  const [loading, setLoading] = useState(false);
  const companyOutlet = useBillingCompanyContext();
  const [search, setSearchText] = useState<string>("");
  const initialLoad = useRef(false);
  const [key, setKey] = React.useState(0);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);

  const fetchSuggestion = async (enteredValue: string) => {
    setLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `&source_name=${enteredValue}`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/leads/get-lead-source?company_outlet_uuid=${company_outlet_uuid}${searchQuery}`;

      const res = await api.get(url);
      const data: ILeadSource[] = res.data.data;
      setOptions(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Stable debounced fetcher tied to the current outlet (avoids per-render
  // allocation + stale closure of useCallback(debounce(...), [])).
  const debounceFn = React.useMemo(
    () => debounce(fetchSuggestion, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );

  React.useEffect(() => () => debounceFn.cancel(), [debounceFn]);

  const hasExactMatch = options.some(
    (opt) =>
      opt?.source_name?.toLowerCase().trim() === search.toLowerCase().trim()
  );

  const showAddIcon =
    !hideAddButton && search.trim().length > 0 && !hasExactMatch && !loading;

  const getOptionLabel = (option: string | ILeadSource) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.source_name) {
      return `${option.source_name}`;
    }
    return `${option.lead_source_uuid || ""}`;
  };

  const handleToggleDialog = () => {
    setOpenDialog(!openDialog);
  };

  const handleFocus = () => {
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setKey((prev) => prev + 1);
      onSelect?.(initialLeadSource);
    },
  }));

  React.useEffect(() => {
    if (search && search !== value) {
      debounceFn(search);
    }
  }, [search, value, debounceFn]);

  React.useEffect(() => {
    if (value === "") {
      setSearchText("");
      setKey((prev) => prev + 1);
    }
  }, [value]);

  return (
    <>
      {/* {label && <CustomFormLabel>{label}</CustomFormLabel>} */}
      <EnrAutoComplete<ILeadSource, false, false, true>
        id="lead-source-auto-search"
        fullWidth
        key={key}
        freeSolo
        isHighlighted={isHighlighted}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          typeof option === "string"
            ? option === value
            : typeof option !== "string" &&
            typeof value !== "string" &&
            option.lead_source_uuid === value.lead_source_uuid
        }
        filterOptions={(x) => x}
        options={options as ILeadSource[]}
        autoComplete
        value={
          (options.find((option) => option.lead_source_uuid === value) ||
            source_name ||
            "") as any
        }
        loading={loading}
        disabled={disabled}
        noOptionsText="Type to search"
        onChange={(event, value: string | ILeadSource | null) => {
          if (typeof value === "object" && value !== null) {
            onSelect?.(value);
          } else {
            onSelect?.(initialLeadSource);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if ((event && event.type === "change") || !newInputValue) {
            setSearchText(newInputValue);
          }
        }}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={label}
            fullWidth
            placeholder="Search by Source"
            error={!!error}
            helperText={helperText}
            onFocus={handleFocus}
            value={params.inputProps.value}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}

                  {showAddIcon && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleDialog();
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        mr: 1,
                      }}
                    >
                      <AddCircleIcon color="primary" fontSize="small" />
                    </Box>
                  )}

                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {openDialog && (
        <CreateLeadSourceDialog
          open={openDialog}
          onClose={handleToggleDialog}
          initialSourceName={search}
          onSuccess={(newLeadSource) => {
            onSelect?.(newLeadSource);
            setSearchText(newLeadSource.source_name || "");
            fetchSuggestion("");
          }}
        />
      )}
    </>
  );
});
